import React from 'react';
import TextSearch from '../../controls/TextSearch';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import GroupForm from './GroupForm';
import GroupPermissionsForm from './GroupPermissionsForm';
import {
  Permission,
  PermissionAction,
  PermissionResource,
  PermissionScope,
} from '../../../data/models/permission.model';
import { UserGroup } from '../../../data/models/user-group.model';
import { ApplicationTitle } from '../../../data/models/application-title.model';
import { UserCategoryType } from '../../../data/models/user-category-type.model';
import * as uiSlice from '../../../data/ui.slice';
import * as securitySlice from '../../../data/security.slice';
import securityUtils from '../../../services/securityUtils';
import ConfirmationDialog from '../../ConfirmationDialog';
import SearchIcon from '@mui/icons-material/Search';

const PREFIX = 'GroupDialog';

const classes = {
  dialog: `${PREFIX}-dialog`,
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  basicInfo: `${PREFIX}-basicInfo`,
  scroll: `${PREFIX}-scroll`,
  permissions: `${PREFIX}-permissions`,
  actions: `${PREFIX}-actions`,
  container: `${PREFIX}-container`,
  noRows: `${PREFIX}-noRows`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialog}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.content}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  [`& .${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
  },
  [`& .${classes.basicInfo}`]: {
    minWidth: 400,
  },
  [`& .${classes.scroll}`]: {
    overflowY: 'auto',
    flex: 1,
  },
  [`& .${classes.permissions}`]: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 400,
  },
  [`& .${classes.actions}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    justifyContent: 'start',
    padding: theme.spacing(2),
  },
  [`& .${classes.noRows}`]: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}));

interface GroupDialogProps {
  open: boolean;
  group?: UserGroup;
  permissions: Permission[];
  applications: ApplicationTitle[];
  categoryTypes: UserCategoryType[];
  resources: PermissionResource[];
  actions: PermissionAction[];
  scopes: PermissionScope[];
  close: (saved: boolean) => void;
}

export default function GroupDialog({
  open,
  group,
  permissions,
  applications,
  categoryTypes,
  resources,
  actions,
  scopes,
  close,
}: GroupDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');
  const title = group ? t('groups.editGroup') : t('groups.createGroup');

  const saving = useAppSelector(securitySlice.selectSavingUserGroup);
  const saved = useAppSelector(securitySlice.selectSavedUserGroup);
  const savedMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [permissionSearch, setPermissionSearch] = React.useState('');
  const [hasPermissionSelection, setHasPermissionSelection] =
    React.useState(false);
  const filteredPermissions = React.useMemo(
    () =>
      permissions.filter(
        (p) =>
          securityUtils
            .getPermissionName(p, resources, actions, scopes)
            .toLowerCase()
            .includes(permissionSearch.toLowerCase()) ||
          (p.description ?? '')
            .toLowerCase()
            .includes(permissionSearch.toLowerCase()),
      ),
    [permissions, permissionSearch, resources, actions, scopes],
  );

  const { control, formState, handleSubmit, reset, watch } = useForm({
    mode: 'onTouched',
  });
  const { isDirty, isValid } = formState;

  const permissionSelections = watch();

  React.useEffect(() => {
    const keys = Object.keys(permissionSelections);
    let hasSelection = keys.some((key) => {
      return key.startsWith('PermissionID_') && permissionSelections[key];
    });
    setHasPermissionSelection(hasSelection);
  }, [permissionSelections]);

  React.useEffect(() => {
    if (open) {
      const defaults: any = {
        ...securityUtils.getGroupDefaults(group, permissions),
      };
      reset(defaults);
    }
  }, [open, group]);

  React.useEffect(() => {
    if (saved && savedMessage) {
      close(true);
    }
  }, [saved, savedMessage]);

  const saveGroup = React.useCallback(
    (data: any) => {
      dispatch(securitySlice.setSavingUserGroup(false));
      dispatch(securitySlice.setSavedUserGroup(false));
      const newGroup = securityUtils.getGroupFromFormData(
        group ? group.userGroupID : undefined,
        data,
        permissions,
      );
      if (group) {
        dispatch(securitySlice.updateUserGroupAsync(newGroup));
      } else {
        dispatch(securitySlice.createUserGroupAsync(newGroup));
      }
    },
    [dispatch, group, permissions],
  );

  const cancelDialog = () => {
    close(false);
    handleConfirmationDialogClose();
  };

  const handleConfirmationDialogOpen = () => {
    setConfirmOpen(true);
  };
  const handleConfirmationDialogClose = () => {
    setConfirmOpen(false);
  };

  const isFormDirty = () => {
    if (isDirty) {
      handleConfirmationDialogOpen();
    } else {
      cancelDialog();
    }
  };

  return (
    <RootDialog
      className={classes.content}
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={isFormDirty}>
      <DialogTitle className={classes.title}>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <Card>
          <CardHeader title={t('groups.basicInfo')} />
          <CardContent className={classes.basicInfo}>
            <GroupForm
              applications={applications}
              categoryTypes={categoryTypes}
              control={control}
              formState={formState}
              requiredFieldMessage={requiredFieldMessage}
              isNewGroup={!group}
              readOnly={false}
            />
          </CardContent>
        </Card>
        <Card className={classes.scroll}>
          <CardHeader title={t('groups.permissions')} />
          <CardContent className={classes.permissions}>
            <Box className={classes.container}>
              <TextSearch
                icon={<SearchIcon />}
                id="group-search"
                label={t('groups.search')}
                placeholder={t('groups.searchPermissionsPlaceholder')}
                currentSearchFilter={permissionSearch}
                onSearchChange={setPermissionSearch}
              />
              {filteredPermissions.length === 0 ? (
                <Box className={classes.noRows}>{t('groups.noRowsFound')}</Box>
              ) : (
                <GroupPermissionsForm
                  permissions={filteredPermissions}
                  resources={resources}
                  actions={actions}
                  scopes={scopes}
                  control={control}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={isFormDirty}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isValid || !isDirty || saving || !hasPermissionSelection}
          onClick={handleSubmit(saveGroup)}>
          {t('common.save')}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmOpen}
        positiveAction={cancelDialog}
        negativeAction={handleConfirmationDialogClose}
      />
    </RootDialog>
  );
}
